'use client';

import { useState } from 'react';
import { distinctTitles } from '@/lib/forms/distinct-titles';
import { useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, ChevronRight, Loader2, Megaphone } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { LoadError } from '@/components/ui/load-error';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { HorizontalScroller } from '@/components/ui/horizontal-scroller';
import {
  listRecruitingPrograms,
  type RecruitingProgram,
  type RecruitingProgramForm,
  type RecruitmentStatus,
} from '@/lib/api/recruitment';

/**
 * 랜딩 "신청 가능한 프로그램": 로그인 없이 신청서 작성으로 들어가는 진입점.
 *
 * 계획: docs/plans/2026-08-18/게스트-신청-에디터-화면.md Phase 2.
 *
 * UI 계약 (AGENTS.md):
 *  - 섹션 자체는 항상 렌더한다. 모집이 없으면 빈 상태 문구를 보여준다 (show/hide 금지).
 *  - 신청할 수 없는 카드는 사라지지 않고 disabled 로 남는다 (오픈 예정/마감/정원 마감).
 *  - 가로 스크롤 한 줄. 모집 수는 6개 이내를 전제한다.
 *  - **카드 하나 = 프로그램 하나.** 신청 서식이 여럿이면(멘토용·멘티용) 카드를 쪼개지 않고
 *    서식마다 버튼을 늘린다 (2026-08-19). 카드를 쪼개면 같은 프로그램명이 반복되면서
 *    가로 줄이 금방 찬다.
 */

/*
 * 열림·닫힘을 색으로 (2026-09-20).
 *
 * 누를 수 있는 것은 **브랜드색이 옅게 깔린** 단추(`soft`), 누를 수 없는 것은 회색
 * 테두리만 남은 흐린 단추다. 상태 자체(모집중·모집 종료)는 배지가 말한다.
 *
 * 꽉 찬 남색으로도 해 봤다가 되돌렸다: 카드 안에 단추가 둘씩 서면 통짜 색 막대가
 * 화면을 눌러 무거웠다. 배지와 같은 옅은 톤으로 맞추니 카드가 한 벌로 읽힌다.
 *
 * 열린 단추를 초록으로 칠해 봤다가 되돌렸다: 단추와 배지가 같은 초록이면
 * 「지금 눌러야 하는 것」과 「지금 상태」가 한 색으로 섞인다. 색은 뜻마다 하나씩이다.
 */

const STATUS_LABEL: Record<RecruitmentStatus, string> = {
  OPEN: '모집중',
  UPCOMING: '오픈 예정',
  FULL: '정원 마감',
  EXPIRED: '모집 종료',
};

export function RecruitingPrograms() {
  const router = useRouter();
  const [startingId, setStartingId] = useState<string | null>(null);
  const { data: programs = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['recruiting-programs'],
    queryFn: listRecruitingPrograms,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  /**
   * 신청 시작 화면으로 보낸다.
   *
   * ⚠ 2026-08-22 로 바뀌었다. 종전에는 여기서 바로 `guest-start` 를 불러 사본을 받고
   * 작성 화면으로 넘어갔다. 이제 그 사이에 **시작 화면 하나**가 있다 — 인적사항 넷과
   * 동의를 받아 계정을 만드는 곳이다(계획 2026-08-22 §4.1). 카드에서 곧장 사본을
   * 만들면 계정도 동의도 없는 사본이 다시 생긴다.
   */
  function handleStart(form: RecruitingProgramForm) {
    // 서식마다 버튼이 서므로 진행 표시는 프로그램이 아니라 링크 단위로 잡는다.
    setStartingId(form.publicLinkId);
    router.push(`/apply-start/${form.publicLinkId}`);
  }

  return (
    <section data-help-anchor="landing-programs" aria-labelledby="recruiting-heading" className="space-y-4">
      <div className="flex items-center justify-center gap-2 text-center">
        <Megaphone className="h-5 w-5 text-primary" />
        <h2 id="recruiting-heading" className="text-xl font-semibold">
          모집 중인 프로그램
        </h2>
      </div>

      {isLoading ? (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} className="h-44 w-72 shrink-0 rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        // 백엔드가 재기동하는 동안 「모집 중인 프로그램이 없습니다」로 보였다(점검 N-S43).
        <LoadError message="모집 중인 프로그램을 불러오지 못했습니다" onRetry={() => void refetch()} />
      ) : programs.length === 0 ? (
        <div className="rounded-lg border bg-muted/30 px-4 py-10 text-center">
          <p className="text-sm font-medium">현재 모집 중인 프로그램이 없습니다</p>
        </div>
      ) : (
        <HorizontalScroller itemCount={programs.length} testId="recruiting-track"
          previousLabel="이전 프로그램 보기" nextLabel="다음 프로그램 보기">
          {programs.map((program) => (
            <ProgramCard
              key={program.programId}
              program={program}
              startingLinkId={startingId}
              disabled={startingId !== null}
              onStart={handleStart}
            />
          ))}
        </HorizontalScroller>
      )}
    </section>
  );
}

function ProgramCard({
  program,
  startingLinkId,
  disabled,
  onStart,
}: {
  program: RecruitingProgram;
  startingLinkId: string | null;
  disabled: boolean;
  onStart: (form: RecruitingProgramForm) => void;
}) {
  const forms = program.forms ?? [];
  // 서식이 하나면 버튼도 하나 — 이름을 고를 일이 없으니 「신청하기」로 끝난다.
  const single = forms.length === 1 ? forms[0] : null;
  // 상태 배지 왼쪽 자리에 올릴 일정. 서식들이 **같은 말을 할 때만** 올라간다.
  const sharedSchedule = sharedScheduleLabel(forms, program.openFrom);
  const note = conciseCardText(program.note, program.name);
  // 버튼 이름은 카드 안 서식들을 **함께** 보고 정한다 — 혼자서는 무엇이 겹치는지 알 수 없다.
  const formLabels = distinctFormLabels(forms, program.name);

  return (
    <div className="flex w-64 shrink-0 flex-col rounded-lg border bg-card p-4">
      <div className="space-y-2">
        {/* 제목은 한 줄을 통째로 쓴다. 배지를 여기 두면 긴 프로그램명이 두세 글자씩
            잘려 읽기 어렵다: 배지는 아래 마감일 줄로 내렸다.

            두 줄 자리를 미리 잡아 두지 않는다(2026-09-20). 카드 배지 높이는 맞았지만
            제목이 한 줄인 카드에 빈 구멍이 남았다 - 그게 어긋난 배지보다 눈에 띈다. */}
        <h3 className="line-clamp-2 break-keep text-sm font-semibold leading-5">
          {program.name}
        </h3>
        {note ? (
          <p className="line-clamp-2 text-xs text-muted-foreground">{note}</p>
        ) : null}
        {/* 샘플 표시는 상태 배지 바로 위 줄에 둔다: "모집중" 을 읽기 전에 눈에 들어와야
            신청 버튼을 누르기 전에 알아챈다. 카드 폭이 좁아 배지 두 개를 한 줄에 놓으면
            마감 문구가 잘린다. */}
        {program.sample ? (
          <Badge variant="warning">테스트용 샘플</Badge>
        ) : null}
        <div className="flex items-center justify-between gap-2">
          {/* 상태 배지 왼쪽은 **일정 자리**다(2026-09-20 사용자 지시). 서식이 여럿이어도
              마감이 서로 같으면 여기 한 번 적는다 — 같은 말이라 누구도 속이지 않는다.
              서로 다를 때만 이 줄을 비우고 각 버튼이 제 마감을 갖는다.
              개수는 적지 않는다(버튼이 바로 아래에 있다). */}
          {sharedSchedule ? (
            <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
              <CalendarDays className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{sharedSchedule}</span>
            </span>
          ) : (
            <span />
          )}
          <StatusBadge status={program.status} />
        </div>
      </div>

      {single ? (
        <Button
          variant={single.status === 'OPEN' ? 'soft' : 'outline'}
          className="mt-4 w-full font-bold"
          size="sm"
          disabled={single.status !== 'OPEN' || disabled}
          onClick={() => onStart(single)}
        >
          {startingLinkId === single.publicLinkId && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {single.status === 'OPEN' ? '지원하기' : '지원 불가'}
          {single.status === 'OPEN' ? (
            <ChevronRight className="ml-1 h-4 w-4 opacity-70" aria-hidden />
          ) : null}
        </Button>
      ) : (
        <div className="mt-4 space-y-2">
          {forms.map((form, index) => (
            <FormButton
              key={form.publicLinkId}
              form={form}
              label={formLabels[index]}
              openFrom={program.openFrom}
              cardStatus={program.status}
              scheduleOnCard={sharedSchedule !== null}
              starting={startingLinkId === form.publicLinkId}
              disabled={disabled}
              onStart={() => onStart(form)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * 신청 서식 버튼 한 개. 이름은 `distinctFormLabels` 가 미리 계산해 넘긴다.
 *
 * 신청할 수 없는 서식도 사라지지 않고 disabled 로 남는다 (카드 전체 규칙과 같다).
 */
function FormButton({
  form,
  label,
  openFrom,
  cardStatus,
  scheduleOnCard,
  starting,
  disabled,
  onStart,
}: {
  form: RecruitingProgramForm;
  label: string;
  openFrom?: string | null;
  /** 카드 배지가 이미 말하고 있는 상태. 같은 말을 버튼이 되풀이하지 않으려고 받는다. */
  cardStatus: RecruitmentStatus;
  /** 카드 줄이 이미 이 일정을 적었는가. 적었으면 버튼은 되풀이하지 않는다. */
  scheduleOnCard: boolean;
  starting: boolean;
  disabled: boolean;
  onStart: () => void;
}) {
  const openable = form.status === 'OPEN';
  // 열려 있을 때만 「신청하기」를 앞에 붙인다. 닫힌 버튼에 붙이면 거짓말이 되고,
  // 밑줄의 「모집 종료」와도 어긋난다.
  const buttonLabel = openable ? `지원하기: ${label}` : label;
  /*
   * 버튼 밑 보조 문구.
   *
   * 열려 있을 때는 **상태를 적지 않는다**(2026-09-20). 종전의 「신청중」은 카드 배지
   * 「모집중」과 같은 말이라 한 카드가 같은 사실을 두 번 말했다. 대신 이 자리에는
   * 배지가 말해 주지 않는 것 — **이 서식의 마감** — 만 둔다. 마감은 프로그램이 아니라
   * 서식(공개링크)이 가지므로 멘토용·멘티용이 서로 다를 수 있다.
   *
   * 마감이 없으면 줄을 비운다.
   *
   * 닫혀 있을 때도 같은 규칙이다(2026-09-20): 카드 배지가 이미 「모집 종료」라고 적었으면
   * 버튼마다 또 적지 않는다. **카드와 다를 때만** 적는다 — 멘토용은 닫히고 멘티용은
   * 열려 있으면 배지는 「모집중」이므로, 닫힌 그 버튼은 제 사정을 스스로 말해야 한다.
   * 시작일은 날짜라서 배지에 없는 정보다. 그건 언제나 적는다.
   *
   * 일정도 같은 규칙을 받는다(`scheduleOnCard`): 서식들의 마감이 서로 같으면 카드 줄이
   * 한 번 적으므로 버튼마다 또 적지 않는다. 서로 다를 때만 버튼이 제 마감을 말한다.
   */
  const schedule = openable
    ? (form.deadline ? scheduleLabel(form, openFrom) : null)
    : form.status === 'UPCOMING' && openFrom
      ? scheduleLabel(form, openFrom)
      : null;
  const statusNote = openable || form.status === cardStatus ? null : STATUS_LABEL[form.status];
  const secondaryLabel = (scheduleOnCard ? null : schedule) ?? statusNote;
  /*
   * 모양(2026-09-20). 이름과 갈 곳 표시(>)를 한 덩어리로 묶어 가운데에 둔다.
   * 못 누르는 줄에는 화살표가 없다 - 갈 곳이 없기 때문이다.
   */
  return (
    <Button
      variant={openable ? 'soft' : 'outline'}
      size="sm"
      className="h-auto w-full justify-center gap-1.5 whitespace-normal px-3 py-2.5 text-center"
      disabled={!openable || disabled}
      onClick={onStart}
    >
      <span className="flex min-w-0 flex-col items-center gap-0.5">
        <span className="flex items-center gap-1 text-xs font-bold leading-4">
          {starting && <Loader2 className="h-3 w-3 shrink-0 animate-spin" />}
          <span className="line-clamp-2">{buttonLabel}</span>
        </span>
        {secondaryLabel ? (
          <span className="text-[11px] font-normal opacity-80">{secondaryLabel}</span>
        ) : null}
      </span>
      {openable ? <ChevronRight className="h-4 w-4 shrink-0 opacity-70" aria-hidden /> : null}
    </Button>
  );
}

/**
 * 한 카드 안 서식들의 버튼 이름 — **서로 겹치는 앞머리를 어절 단위로 덜어낸다.**
 *
 * 문제(2026-09-20): 서식 제목이 프로그램 이름을 통째로 다시 말한다.
 *
 *   프로그램  `26년2학기SW전공멘토링`
 *   서식      `2026학년도 2학기 SW전공 멘토링 신청서 (멘토용)`
 *             `2026학년도 2학기 SW전공 멘토링 신청서 (멘티용)`
 *
 * 프로그램명으로 잘라내려 해도 안 맞는다 — 같은 것을 가리키지만 표기가 다르다
 * (`26년` vs `2026학년도`, 띄어쓰기). 그래서 프로그램 이름을 보지 않고 **서식들끼리**
 * 비교한다. 카드 안에서 겹치는 앞머리는 정의상 구별에 쓸모가 없고, 프로그램 이름도
 * 「신청서」도 거기에 함께 들어 있어 한 번에 빠진다.
 *
 * 남는 것은 담당자가 구별하려고 붙인 꼬리뿐이다: `(멘토용)` · `(멘티용)`.
 *
 * 규칙:
 *  - **어절 경계**에서만 자른다. 글자 단위로 자르면 `(멘` 같은 토막이 남는다.
 *  - 적어도 한 어절은 남긴다.
 *  - **덜어낸 뒤에도 서로 달라야 덜어낸다.** 제목이 아예 같으면(`멘토링 신청서` 둘)
 *    깎아 봐야 구별되지 않으면서 이름만 짧아진다 — 그럴 땐 원래 제목을 쓴다.
 *  - 남은 것이 통째로 괄호 안이면 괄호를 벗긴다 — 앞말이 사라진 `(멘토용)` 의 괄호는
 *    붙일 곳을 잃은 꼬리표다.
 *  - 서식이 하나뿐이면 비교할 상대가 없으므로 종전대로 프로그램명 접두사만 본다.
 */
function distinctFormLabels(forms: RecruitingProgramForm[], programName: string): string[] {
  // 규칙 본문은 `lib/forms/distinct-titles` 에 있다 - 신청확인 화면이 같은 것을 쓴다.
  return distinctTitles(forms.map((form) => form.title))
    ?? forms.map((form) => conciseCardText(form.title, programName) ?? form.title);
}

/**
 * 카드 제목에서 이미 보여 준 프로그램명이 안내문·서식 제목의 맨 앞에 그대로
 * 반복될 때만 덜어낸다. 포함 관계나 학기 패턴으로 추측하지 않으므로 서로 다른
 * 프로그램·서식 이름은 원문 그대로 남는다.
 */
function conciseCardText(value: string | null | undefined, programName: string): string | null {
  const text = value?.trim();
  const prefix = programName.trim();
  if (!text) return null;
  if (!prefix || !text.startsWith(prefix)) return text;

  const remainder = text.slice(prefix.length).trim().replace(/^[-–—:·]\s*/, '');
  return remainder || text;
}

function StatusBadge({ status }: { status: RecruitmentStatus }) {
  switch (status) {
    case 'OPEN':
      return <Badge variant="success" className="shrink-0">{STATUS_LABEL.OPEN}</Badge>;
    case 'UPCOMING':
      return (
        <Badge variant="outline" className="shrink-0">
          {STATUS_LABEL.UPCOMING}
        </Badge>
      );
    case 'FULL':
      return (
        <Badge variant="secondary" className="shrink-0">
          {STATUS_LABEL.FULL}
        </Badge>
      );
    case 'EXPIRED':
      return (
        <Badge variant="secondary" className="shrink-0">
          {STATUS_LABEL.EXPIRED}
        </Badge>
      );
  }
}

/**
 * 카드 줄(상태 배지 왼쪽)에 올릴 일정 — **서식들이 모두 같은 말을 할 때만** 올린다.
 *
 * 2026-09-20 사용자 지시로 이 자리를 채운다. 종전에는 서식이 둘 이상이면 무조건
 * 비웠다. 마감은 프로그램이 아니라 서식(공개링크)이 가지므로 멘토용·멘티용이 서로
 * 다를 수 있고, 하나만 적으면 나머지를 속이기 때문이었다. 그런데 **같은 날일 때는
 * 속일 것이 없다** — 실제 등록분도 둘 다 같은 마감이다. 그래서 규칙을 「서식이
 * 하나일 때」에서 「서식들이 한목소리일 때」로 넓혔다. 서로 다르면 종전대로 비우고
 * 각 버튼이 제 마감을 갖는다.
 *
 * 카드 배지가 못 하는 말만 올린다:
 *  - **시작일 없는 「오픈 예정」은 비운다.** 적을 날짜가 시작일이 아니라 마감뿐인데,
 *    아직 열리지도 않은 모집에 마감을 앞세우면 읽는 사람을 헷갈리게 한다.
 *  - **마감이 없는데 열려 있지도 않으면 비운다.** 마감 없이 닫힌 서식은 날짜가 아니라
 *    정원·수동으로 닫힌 것이라 「상시 모집」이 거짓이 된다. 열려 있을 때만 그렇게 적는다.
 */
function sharedScheduleLabel(
  forms: RecruitingProgramForm[],
  openFrom?: string | null,
): string | null {
  if (forms.length === 0) return null;
  const labels = forms.map((form) => cardScheduleLabel(form, openFrom));
  if (labels.some((label) => label === null)) return null;
  return labels.every((label) => label === labels[0]) ? labels[0] : null;
}

function cardScheduleLabel(form: RecruitingProgramForm, openFrom?: string | null): string | null {
  if (form.status === 'UPCOMING') return openFrom ? scheduleLabel(form, openFrom) : null;
  // 닫힌 서식은 날짜를 적지 않는다. 배지 「모집 종료」·「정원 마감」이 같은 사실을 이미 말한다(10-03 답지 U6).
  // 줄은 배지가 지키므로 자리는 그대로다.
  if (form.status === 'EXPIRED' || form.status === 'FULL') return null;
  if (!form.deadline) return form.status === 'OPEN' ? '상시 모집' : null;
  return scheduleLabel(form, openFrom);
}

/**
 * 마감 문구. 오른쪽에 상태 배지가 나란히 서므로 좁은 카드에서 잘리지 않게 짧게 쓴다.
 * 연도는 뺀다: 모집은 근시일 안에 끝나고, 정확한 날짜는 신청 화면에서 다시 보인다.
 *
 * 마감은 서식(공개링크)이, 시작일은 프로그램이 갖는다 — 그래서 인자가 둘이다.
 */
function scheduleLabel(form: RecruitingProgramForm, openFrom?: string | null): string {
  if (form.status === 'UPCOMING' && openFrom) {
    return `${monthDay(openFrom)} 시작`;
  }
  if (!form.deadline) {
    return '상시 모집';
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${form.deadline}T00:00:00`);
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  if (Number.isNaN(days)) return `마감 ${monthDay(form.deadline)}`;
  if (days < 0) return `${monthDay(form.deadline)} 마감됨`;
  if (days === 0) return '오늘 마감';
  return `D-${days} · ${monthDay(form.deadline)} 마감`;
}

/** `2026-09-05` → `09.05` */
function monthDay(isoDate: string): string {
  const [, month, day] = isoDate.split('-');
  return month && day ? `${month}.${day}` : isoDate;
}
